// src/rms/ads/adEngine.js
// ============================================================================
// Ad selection, rotation and pacing
// ============================================================================
// Three rules from the monetization model are enforced here, in one place, so
// no screen can opt out of them by accident:
//
//   1. NEVER during a transaction. A slot marked adsAllowed: false returns no
//      ad, ever. Checkout, payment, signature capture and damage inspection are
//      registered that way.
//   2. Rotate on a timer (20-30s) to lift impressions without extra clicks.
//   3. Frequency-cap the *refresh*: a new creative is only picked every 3-5
//      staff actions, not on every single click. Without this the ad churns on
//      every keystroke-driven re-render, which is what makes ad-supported
//      software feel hostile.
// ============================================================================

import db from "../data";
import { getCampaigns, getSlot, CPM_FLOOR_CENTS } from "./adInventory";
import { isAdSenseConfigured, adSenseCampaign } from "./adsense";

// ----------------------------------------------------------------------------
// Action pacing
// ----------------------------------------------------------------------------

/**
 * In-page actions that must elapse before slots pick new creative.
 *
 * This paces ads DURING a screen — typing, saving, filtering — so the rail does
 * not churn on every keystroke-driven re-render. Navigation between sections is
 * handled separately by rotateNow() and is NOT capped: every section change
 * rotates the whole set.
 */
const ACTIONS_PER_REFRESH = 4;

// The rotation counter must OUTLIVE a page load. With hard-reload-on-navigation
// enabled, module state resets on every section change, so an in-memory counter
// would restart at zero and re-select the identical set of advertisers every
// time — the exact opposite of what the reload is for.
const EPOCH_KEY = "rms.ad.epoch";

function readEpoch() {
  try {
    return parseInt(window.sessionStorage.getItem(EPOCH_KEY) || "0", 10) || 0;
  } catch {
    return 0;
  }
}

function persistEpoch(value) {
  try {
    window.sessionStorage.setItem(EPOCH_KEY, String(value));
  } catch {
    /* private mode — rotation still works within this page */
  }
}

let actionCount = 0;
let refreshEpoch = readEpoch();
const listeners = new Set();

function emit() {
  refreshEpoch += 1;
  persistEpoch(refreshEpoch);
  listeners.forEach((fn) => fn(refreshEpoch));
}

/**
 * Records an in-page staff action (save, search, filter). Rotates only once the
 * cap is reached.
 */
export function recordAction() {
  actionCount += 1;
  if (actionCount >= ACTIONS_PER_REFRESH) {
    actionCount = 0;
    emit();
  }
}

/**
 * Forces an immediate rotation, bypassing the frequency cap. Called on every
 * move between sections (Dashboard → Billing → Inventory …) so each section
 * change serves a fresh set of advertisers.
 */
export function rotateNow() {
  actionCount = 0;
  emit();
}

export function subscribeToRefresh(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

export function currentEpoch() {
  return refreshEpoch;
}

// ----------------------------------------------------------------------------
// Selection
// ----------------------------------------------------------------------------

/** Weighted pick. `nonce` lets the caller advance the rotation deterministically. */
function weightedPick(campaigns, nonce) {
  if (!campaigns.length) return null;
  const total = campaigns.reduce((sum, c) => sum + (c.weight || 1), 0);
  // Hash the nonce into the range so rotation is stable per render rather than
  // re-randomising on every React commit.
  let target = ((Math.sin(nonce * 12.9898) * 43758.5453) % 1 + 1) % 1 * total;
  for (const campaign of campaigns) {
    target -= campaign.weight || 1;
    if (target <= 0) return campaign;
  }
  return campaigns[campaigns.length - 1];
}

/**
 * Which demand a slot draws from, in priority order.
 *
 *   1. SOLD  -- database-backed campaigns with a real budget behind them.
 *   2. ADSENSE -- network fill, when configured and not already used in this
 *      stack. See adsense.js for why this is capped at one per placement.
 *   3. HOUSE -- the static inventory, which also keeps a rail looking full.
 *
 * Sold demand outranking network fill is the whole point: a slot somebody paid
 * a rate card for must never be given away to a network for a fraction of it.
 */
function demandFor({ slot, config, pools, used }) {
  const sold = (pools?.[slot.placement] || []).filter((c) => !used.has(c.id));
  if (sold.length) return sold;

  const network = adSenseCampaign(slot.placement);
  if (isAdSenseConfigured(slot.placement) && !used.has(network.id)) return [network];

  return getCampaigns(config.adCategory, slot.placement, CPM_FLOOR_CENTS).filter(
    (c) => !used.has(c.id)
  );
}

/**
 * Returns the creative to show, or null when no ad may be served.
 * Null is returned — rather than a placeholder — whenever:
 *   - the operator is on the ad-free paid tier
 *   - the slot is a transactional surface
 *   - there is no eligible demand
 */
export function selectAd({ operator, config, slotKey, nonce = 0 }) {
  if (!operator || !config) return null;
  if (!operator.ads_enabled || operator.plan_tier === "ad_free") return null;

  const slot = getSlot(slotKey);
  if (!slot || !slot.adsAllowed) return null;

  const campaigns = demandFor({ slot, config, pools: null, used: new Set() });
  if (!campaigns.length) return null;

  return { slot, campaign: weightedPick(campaigns, nonce) };
}

/**
 * Fills a stack of slots (the right rail) with DISTINCT advertisers.
 *
 * Repeating one advertiser four times down a rail reads as broken rather than
 * busy, and burns four impressions an advertiser will not pay for twice. So
 * this returns at most one placement per campaign and simply yields fewer units
 * when a vertical has thin demand — a short rail beats a repetitive one.
 *
 * Returns [{ slotKey, campaign }], shorter than slotKeys when demand runs out.
 */
export function selectAdStack({ operator, config, slotKeys, nonce = 0, pools = null }) {
  if (!operator || !config) return [];
  if (!operator.ads_enabled || operator.plan_tier === "ad_free") return [];

  const used = new Set();
  const filled = [];

  slotKeys.forEach((slotKey, index) => {
    const slot = getSlot(slotKey);
    if (!slot || !slot.adsAllowed) return;

    // `pools` carries database-backed sold demand, keyed by placement. When it
    // is absent — first render, or a load failure — demandFor falls through to
    // network fill and then to the static inventory, so a slot is never blank.
    const available = demandFor({ slot, config, pools, used });
    if (!available.length) return;

    const campaign = weightedPick(available, nonce + index * 97);
    used.add(campaign.id);
    filled.push({ slot, slotKey, campaign });
  });

  return filled;
}

// ----------------------------------------------------------------------------
// Measurement — hybrid CPM + CPC needs both sides recorded
// ----------------------------------------------------------------------------

/**
 * Records a VIEWABLE impression. Called from AdUnit only after the MRC
 * threshold is met, never on render — so these counts are the ones an
 * advertiser would actually pay for.
 */
export async function logImpression({ operator, config, slotKey, campaign, screen = null }) {
  if (!operator || !campaign) return;

  // rms_ad_impressions is the platform's record of what a DIRECT advertiser was
  // charged for -- it is excluded from the operator export for exactly that
  // reason. A network fill was not bought from us and must not appear in it, or
  // every avails figure and pacing budget computed from that table is inflated
  // by inventory nobody sold. Google counts its own delivery; read it there.
  if (campaign.isAdsense) return;
  try {
    await db.create("adImpressions", {
      operator_id: operator.id,
      campaign_id: campaign.id,
      creative_id: campaign.creativeId || null,
      slot_key: slotKey,
      screen,
      ad_category: config?.adCategory || null,
      is_viewable: true,
      occurred_at: new Date().toISOString(),
    });

    // Sold campaigns bill against a budget, so delivery has to be accrued as it
    // happens — pacing and budget exhaustion both read from it on the next fill.
    if (campaign.isDirectSold) {
      await accrueDelivery({ campaign, operatorId: operator.id, impressions: 1, clicks: 0 });
    }
  } catch (err) {
    // Measurement must never break the operator's workflow.
    console.warn("Ad impression not recorded:", err);
  }
}

export async function logClick({ operator, config, slotKey, campaign, screen = null }) {
  if (!operator || !campaign) return;
  if (campaign.isAdsense) return; // See logImpression.
  try {
    await db.create("adClicks", {
      operator_id: operator.id,
      campaign_id: campaign.id,
      creative_id: campaign.creativeId || null,
      slot_key: slotKey,
      screen,
      ad_category: config?.adCategory || null,
      occurred_at: new Date().toISOString(),
    });

    if (campaign.isDirectSold) {
      await accrueDelivery({ campaign, operatorId: operator.id, impressions: 0, clicks: 1 });
    }
  } catch (err) {
    console.warn("Ad click not recorded:", err);
  }
}

/**
 * Accrues delivery and spend against a sold campaign.
 *
 * Writes both the daily rollup (what an advertiser is invoiced and reported on)
 * and the running totals on the campaign (what budget and pacing checks read).
 * Keeping the rollup means a 90-day flight report doesn't scan every raw event.
 */
export async function accrueDelivery({ campaign, operatorId, impressions = 0, clicks = 0 }) {
  const day = new Date().toISOString().slice(0, 10);
  const spend =
    Math.round(((campaign.cpmCents || 0) * impressions) / 1000) + (campaign.cpcCents || 0) * clicks;

  try {
    const existing =
      ((await db.list("adDeliveryDaily", { campaign_id: campaign.id })).data || []).find(
        (d) => d.day === day && (d.creative_id || null) === (campaign.creativeId || null)
      ) || null;

    if (existing) {
      await db.update("adDeliveryDaily", existing.id, {
        viewable_impressions: (existing.viewable_impressions || 0) + impressions,
        clicks: (existing.clicks || 0) + clicks,
        spend_cents: (existing.spend_cents || 0) + spend,
      });
    } else {
      await db.create("adDeliveryDaily", {
        campaign_id: campaign.id,
        creative_id: campaign.creativeId || null,
        day,
        viewable_impressions: impressions,
        clicks,
        unique_operators: operatorId ? 1 : 0,
        spend_cents: spend,
      });
    }

    const record = (await db.get("adCampaigns", campaign.id)).data;
    if (record) {
      await db.update("adCampaigns", campaign.id, {
        delivered_impressions: (record.delivered_impressions || 0) + impressions,
        spent_cents: (record.spent_cents || 0) + spend,
        // A flight that has met its goal or budget stops on its own rather than
        // waiting for someone to notice and pause it.
        status:
          (record.budget_cents != null && (record.spent_cents || 0) + spend >= record.budget_cents) ||
          (record.goal_impressions != null &&
            (record.delivered_impressions || 0) + impressions >= record.goal_impressions)
            ? "completed"
            : record.status,
      });
    }
  } catch (err) {
    console.warn("Delivery not accrued:", err);
  }
}

/** Revenue estimate: CPM on viewable impressions + CPC on clicks. */
export function estimateRevenueCents(impressions, clicks, campaignsById) {
  const cpm = impressions.filter((i) => i.is_viewable === true).reduce((sum, i) => {
    const c = campaignsById[i.campaign_id];
    return sum + (c ? (c.cpmCents || 0) / 1000 : 0);
  }, 0);
  const cpc = clicks.reduce((sum, k) => {
    const c = campaignsById[k.campaign_id];
    return sum + (c ? c.cpcCents || 0 : 0);
  }, 0);
  return Math.round(cpm + cpc);
}

/**
 * Revenue broken down by a key — "slot_key" or "screen".
 *
 * RPM (revenue per thousand impressions) is the number that tells you whether a
 * placement earns its space. A slot with high volume and low RPM is costing
 * goodwill for nothing and should be removed.
 */
export function breakdownBy(field, impressions, clicks, campaignsById) {
  const rows = {};

  // Only rows explicitly marked viewable count. Anything logged before
  // viewability gating existed was recorded on render and would overstate
  // delivery, so it is excluded rather than quietly inflating the numbers.
  const counted = impressions.filter((i) => i.is_viewable === true);

  const bucket = (key) => {
    if (!rows[key]) rows[key] = { key, impressions: 0, clicks: 0, revenueCents: 0 };
    return rows[key];
  };

  counted.forEach((i) => {
    const r = bucket(i[field] || "unknown");
    r.impressions += 1;
    const c = campaignsById[i.campaign_id];
    if (c) r.revenueCents += (c.cpmCents || 0) / 1000;
  });

  clicks.forEach((k) => {
    const r = bucket(k[field] || "unknown");
    r.clicks += 1;
    const c = campaignsById[k.campaign_id];
    if (c) r.revenueCents += c.cpcCents || 0;
  });

  return Object.values(rows)
    .map((r) => ({
      ...r,
      revenueCents: Math.round(r.revenueCents),
      ctr: r.impressions ? (r.clicks / r.impressions) * 100 : 0,
      // RPM is per thousand impressions, so it stays comparable across slots
      // with very different volumes.
      rpmCents: r.impressions ? Math.round((r.revenueCents / r.impressions) * 1000) : 0,
    }))
    .sort((a, b) => b.revenueCents - a.revenueCents);
}

const adEngine = {
  recordAction,
  rotateNow,
  subscribeToRefresh,
  currentEpoch,
  selectAd,
  selectAdStack,
  logImpression,
  logClick,
  estimateRevenueCents,
  ACTIONS_PER_REFRESH,
};

export default adEngine;
